import { z } from 'zod';
import { ownerDeviceNetworkSchema } from '@atum-remote/protocol/device-network';
import { browserNyxConnection } from './nyx-session.js';
import {
  browserFilesConnection,
  browserPrivilegeConnection,
  browserDeviceActionConnection,
  browserInspectionConnection,
} from './files-session.js';
import {
  deviceActionSchema,
  deviceActionResultSchema,
  type DeviceActionRequest,
} from '@atum-remote/protocol/device-actions';
import { clearNyxRecovery } from './nyx-recovery.js';
import {
  beginBrowserSignIn,
  browserTabHeaders,
  browserTabKey,
  clearBrowserTab,
  navigateBrowserTab,
  persistBrowserTab,
} from './lib/browser-tab.js';
import {
  preferencesSchema,
  type AccountPreferences,
  type PreferenceMutation,
} from '@atum-remote/preferences';
import {
  previewTools,
  type ConnectionTool,
  type DeviceSnapshot,
  type RemoteDevice,
  type RemoteUiAdapter,
} from './model.js';

const identity = z.object({
  configured: z.boolean(),
  account: z
    .object({
      name: z.string(),
      access: z.enum(['owner', 'personal', 'staff']).optional(),
      issuer: z.string().optional(),
      subject: z.string().optional(),
    })
    .nullable(),
});
const unauthorizedResponse = Symbol('unauthorized');
const devicePage = z.object({
  data: z.array(
    z.object({
      id: z.string().uuid(),
      displayName: z.string(),
      hostname: z.string(),
      platform: z.string(),
      osVersion: z.string().nullable(),
      architecture: z.string().nullable().optional(),
      appVersion: z.string().nullable().optional(),
      // Presentation only. A malformed value hides the addresses, never the device.
      network: ownerDeviceNetworkSchema.nullable().optional().catch(null),
      status: z.enum(['online', 'offline', 'revoked']),
      lastSeenAt: z.string().datetime().nullable(),
      capabilities: z
        .array(
          z.enum([
            'terminal',
            'desktop.view',
            'desktop.control',
            'uia',
            'nyx.read',
            'nyx.control',
            'files.read',
            'files.write',
            'linux.sudo.service',
          ]),
        )
        .default([]),
    }),
  ),
  pagination: z.object({
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
  }),
});

/** Who the server says is signed in, and whether owner sign-in is configured at all. */
export interface BrowserSession {
  readonly configured: boolean;
  readonly account: { readonly name: string } | null;
}

/** The browser holds only a same-origin HttpOnly session cookie. OAuth credentials stay on the server. */
export class ServerAdapter implements RemoteUiAdapter {
  readonly kind = 'web' as const;
  readonly supportsSavedConnections = false;
  readonly supportsSignIn = true;
  signInConfigured = false;
  accountName: string | undefined;
  accountAccess: 'owner' | 'personal' | 'staff' | undefined;
  onSessionEnded: (() => void) | undefined;
  readonly apiBaseUrl: string;
  #revision = 0;
  #sessionCheckedAt = 0;
  #signInAbort: AbortController | undefined;
  get sessionCheckedAt(): number {
    return this.#sessionCheckedAt;
  }
  constructor(
    origin: string,
    base = '/api',
    private readonly request: typeof fetch = globalThis.fetch.bind(globalThis),
  ) {
    const url = new URL(base, origin);
    if (url.origin !== origin || url.username || url.password || url.search || url.hash)
      throw new Error('The website API must use the same origin as this page.');
    this.apiBaseUrl = url.href.replace(/\/$/, '');
  }
  async signIn(): Promise<void> {
    if (!this.signInConfigured) return;
    this.cancelSignIn();
    const proof = beginBrowserSignIn();
    const controller = new AbortController();
    this.#signInAbort = controller;
    const popup = window.open('about:blank', '_blank', 'popup,width=600,height=760');
    let accepted = false;
    try {
      if (!popup) throw new Error('Allow the ATUM Auth sign-in window and try again.');
      popup.opener = null;
      const result = z
        .object({ location: z.url() })
        .parse(await this.#get('/browser/login', controller.signal, 'POST'));
      popup.location.replace(result.location);
      const until = Date.now() + 300_000;
      // The original document stays alive while Auth runs separately. Checking
      // identity does not extend inactivity authority or grant another tab access.
      while (Date.now() < until) {
        controller.signal.throwIfAborted();
        const current = await this.session(controller.signal);
        controller.signal.throwIfAborted();
        if (current.account) {
          persistBrowserTab(proof);
          accepted = true;
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      throw new Error('Sign-in expired. Close the sign-in window and try again.');
    } finally {
      popup?.close();
      if (!accepted && browserTabKey() === proof) clearBrowserTab();
      if (this.#signInAbort === controller) this.#signInAbort = undefined;
    }
  }
  cancelSignIn() {
    this.#signInAbort?.abort();
  }
  async activity(): Promise<void> {
    await this.#get('/browser/activity', undefined, 'POST');
  }
  async signOut(): Promise<void> {
    this.cancelSignIn();
    this.#revision++;
    await this.#get('/browser/logout', undefined, 'POST');
    clearNyxRecovery(this);
    clearBrowserTab();
    this.accountName = undefined;
    this.accountAccess = undefined;
  }
  async #get(
    path: string,
    signal?: AbortSignal,
    method = 'GET',
    unauthorized: 'throw' | 'null' = 'throw',
    body: unknown = {},
  ): Promise<unknown> {
    const response = await this.request(`${this.apiBaseUrl}${path}`, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      redirect: 'error',
      headers: {
        ...browserTabHeaders(),
        ...(path === '/browser/preferences' ? { 'x-atum-preferences-version': '2' } : {}),
        ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(method === 'POST' ? { body: JSON.stringify(body) } : {}),
      signal: signal
        ? AbortSignal.any([signal, AbortSignal.timeout(15_000)])
        : AbortSignal.timeout(15_000),
    });
    if (!response.ok) {
      if (response.status === 401) {
        clearBrowserTab();
        clearNyxRecovery(this);
        this.accountName = undefined;
        this.accountAccess = undefined;
        this.onSessionEnded?.();
        if (unauthorized === 'null') return unauthorizedResponse;
        throw new Error('Your session ended. Sign in again to view devices.');
      }
      if (path === '/browser/preferences' || path.startsWith('/browser/devices/')) {
        const problem = z
          .object({ error: z.string().max(300) })
          .safeParse(await response.json().catch(() => null));
        if (problem.success) throw new Error(problem.data.error);
      }
      throw new Error(`The server could not complete this request (${response.status}).`);
    }
    return response.json();
  }
  async loadPreferences(): Promise<AccountPreferences> {
    const revision = this.#revision;
    const value = preferencesSchema.parse(await this.#get('/browser/preferences'));
    if (revision !== this.#revision) throw new Error('Your account session changed.');
    return value;
  }
  async writePreferences(
    expectedRevision: number,
    mutation: PreferenceMutation,
  ): Promise<AccountPreferences> {
    const revision = this.#revision;
    const value = preferencesSchema.parse(
      await this.#get('/browser/preferences', undefined, 'POST', 'throw', {
        expectedRevision,
        mutation,
      }),
    );
    if (revision !== this.#revision) throw new Error('Your account session changed.');
    return value;
  }
  /**
   * Identity only, for the sign-in gate. A 401 here simply means nobody is signed in;
   * any other failure is the server being unreachable or unsupported.
   */
  async session(signal?: AbortSignal): Promise<BrowserSession> {
    const revision = this.#revision;
    const raw = await this.#get('/browser/session', signal, 'GET', 'null');
    if (revision !== this.#revision)
      throw new DOMException('Session changed. Refresh to continue.', 'AbortError');
    if (raw === unauthorizedResponse) {
      this.#sessionCheckedAt = Date.now();
      return { configured: this.signInConfigured, account: null };
    }
    const parsed = identity.safeParse(raw);
    if (!parsed.success) throw new Error('The server returned an unsupported sign-in response.');
    this.signInConfigured = parsed.data.configured;
    this.accountName = parsed.data.account?.name;
    this.accountAccess = parsed.data.account?.access;
    if (!parsed.data.account) clearNyxRecovery(this);
    this.#sessionCheckedAt = Date.now();
    return parsed.data;
  }
  async load(signal?: AbortSignal): Promise<DeviceSnapshot> {
    const revision = this.#revision;
    const session = identity.safeParse(await this.#get('/browser/session', signal));
    if (!session.success) throw new Error('The server returned an unsupported sign-in response.');
    if (revision !== this.#revision) throw new Error('Session changed. Refresh to continue.');
    this.signInConfigured = session.data.configured;
    this.accountName = session.data.account?.name;
    this.accountAccess = session.data.account?.access;
    this.#sessionCheckedAt = Date.now();
    if (!session.data.account) {
      clearNyxRecovery(this);
      return { devices: [], activity: [] };
    }
    const devices: DeviceSnapshot['devices'][number][] = [];
    let offset = 0;
    for (;;) {
      const parsed = devicePage.safeParse(
        await this.#get(`/browser/devices?limit=100&offset=${offset}`, signal),
      );
      if (revision !== this.#revision) throw new Error('Session changed. Refresh to continue.');
      if (!parsed.success) throw new Error('The server returned an unsupported device response.');
      const page = parsed.data;
      devices.push(
        ...page.data.map((device): DeviceSnapshot['devices'][number] => ({
          id: device.id,
          ...(device.platform === 'linux' ? { hostProfile: 'linux-server' as const } : {}),
          personalAccess: session.data.account?.access === 'personal',
          name: device.displayName,
          hostname: device.hostname,
          reportedPlatform: device.platform,
          platform:
            device.platform === 'windows' ||
            device.platform === 'macos' ||
            device.platform === 'linux'
              ? device.platform
              : 'unknown',
          osVersion: device.osVersion ?? '',
          ...(device.architecture ? { architecture: device.architecture } : {}),
          ...(device.appVersion ? { appVersion: device.appVersion } : {}),
          ...(device.network
            ? {
                network: {
                  ...(device.network.primaryIpv4
                    ? { primaryIpv4: device.network.primaryIpv4 }
                    : {}),
                  ...(device.network.primaryIpv6
                    ? { primaryIpv6: device.network.primaryIpv6 }
                    : {}),
                  addresses: device.network.addresses ?? [],
                },
              }
            : {}),
          presence: device.status,
          group: 'My devices',
          source: 'managed',
          lastSeenAt: device.lastSeenAt,
          desktop: {
            controlAvailable: device.capabilities.includes('desktop.control'),
            available: device.status === 'online' && device.capabilities.includes('desktop.view'),
            explanation:
              device.platform === 'linux'
                ? 'Headless server — managed without a graphical desktop.'
                : device.status !== 'online'
                  ? 'This device is offline.'
                  : device.capabilities.includes('desktop.view')
                    ? device.capabilities.includes('desktop.control')
                      ? 'View and control this device in your browser.'
                      : 'View this device in your browser.'
                    : 'Desktop access has not been granted.',
          },
          terminal: {
            available: device.status === 'online' && device.capabilities.includes('terminal'),
            explanation:
              device.status !== 'online'
                ? 'This device is offline.'
                : device.capabilities.includes('terminal')
                  ? device.platform === 'linux'
                    ? 'Open Bash in your browser.'
                    : device.platform === 'macos'
                      ? 'Open zsh in your browser.'
                      : 'Open PowerShell in your browser.'
                  : 'Terminal access has not been granted.',
          },
          nyx: {
            available: device.status === 'online' && device.capabilities.includes('nyx.read'),
            controlAvailable: device.capabilities.includes('nyx.control'),
            explanation:
              'Requires Nyx Link running with ATUM Remote enabled, the same Atum account, and an explicit Nyx device grant.',
          },
          ...previewTools,
          resources: {
            available:
              session.data.account?.access !== 'personal' &&
              device.platform === 'linux' &&
              device.status === 'online' &&
              device.capabilities.includes('terminal'),
            explanation:
              device.status !== 'online'
                ? 'Connect when this server is online.'
                : device.platform !== 'linux'
                  ? 'Server inspection is available on Linux hosts.'
                  : !device.capabilities.includes('terminal')
                    ? 'Server inspection requires terminal access.'
                    : 'Read CPU, memory, disk, network, processes and services as the enrolled account.',
          },
          logs: {
            available:
              session.data.account?.access !== 'personal' &&
              device.platform === 'linux' &&
              device.status === 'online' &&
              device.capabilities.includes('terminal'),
            explanation:
              device.status !== 'online'
                ? 'Connect when this server is online.'
                : device.platform !== 'linux'
                  ? 'System journal inspection is available on Linux hosts.'
                  : !device.capabilities.includes('terminal')
                    ? 'Server inspection requires terminal access.'
                    : 'Read the system journal within the enrolled account’s permissions.',
          },
          privileged: {
            available:
              device.status === 'online' && device.capabilities.includes('linux.sudo.service'),
            explanation:
              'Each service operation requires fresh passkey approval and an installed host policy.',
          },
          files: {
            available: device.status === 'online' && device.capabilities.includes('files.read'),
            controlAvailable: device.capabilities.includes('files.write'),
            explanation: 'Access the folder selected on this device.',
          },
        })),
      );
      offset += page.data.length;
      if (offset >= page.pagination.total) return { devices, activity: [] };
      if (!page.data.length || offset >= 10000)
        throw new Error('The device list could not be loaded completely.');
    }
  }
  async saveConnection(): Promise<void> {
    throw new Error('Save SSH connections in the installed app.');
  }
  async openFiles(device: RemoteDevice) {
    if (!device.files.available) throw new Error('Files are unavailable for this device.');
    const lease = await this.#get('/browser/sessions', undefined, 'POST', undefined, {
      deviceId: device.id,
      capabilities: device.files.controlAvailable ? ['files.read', 'files.write'] : ['files.read'],
    });
    return browserFilesConnection(this.apiBaseUrl, lease, (id) =>
      this.#get(`/browser/sessions/${id}/close`, undefined, 'POST'),
    );
  }
  async openInspection(device: RemoteDevice) {
    if (
      device.personalAccess ||
      !device.resources.available ||
      device.hostProfile !== 'linux-server'
    )
      throw new Error('Server inspection is unavailable for this device.');
    const lease = await this.#get('/browser/sessions', undefined, 'POST', 'throw', {
      deviceId: device.id,
      capabilities: ['terminal'],
    });
    return browserInspectionConnection(this.apiBaseUrl, lease, (id) =>
      this.#get(`/browser/sessions/${id}/close`, undefined, 'POST'),
    );
  }
  async openPrivileges(device: RemoteDevice) {
    if (device.personalAccess || !device.privileged?.available)
      throw new Error('Privileged service access is unavailable.');
    const lease = await this.#get('/browser/sessions', undefined, 'POST', 'throw', {
      deviceId: device.id,
      capabilities: ['linux.sudo.service'],
    });
    return browserPrivilegeConnection(this.apiBaseUrl, lease, (id) =>
      this.#get(`/browser/sessions/${id}/close`, undefined, 'POST'),
    );
  }
  /** Retires a registered device for the whole account. The server revokes it and ends its sessions. */
  async removeConnection(id: string): Promise<void> {
    if (this.accountAccess !== 'owner')
      throw new Error('Only the owner account can remove devices.');
    z.uuid().parse(id);
    await this.#get(`/browser/devices/${id}/remove`, undefined, 'POST');
  }
  async runDeviceAction(device: RemoteDevice, input: DeviceActionRequest) {
    const request = deviceActionSchema.parse(input);
    if (
      device.personalAccess ||
      !device.terminal.available ||
      device.source !== 'managed' ||
      !['windows', 'macos'].includes(device.platform)
    )
      throw new Error('Device actions require an online owner connection.');
    const lease = await this.#get('/browser/sessions', undefined, 'POST', 'throw', {
      deviceId: device.id,
      capabilities: ['terminal'],
    });
    const connection = await browserDeviceActionConnection(this.apiBaseUrl, lease, (id) =>
      this.#get(`/browser/sessions/${id}/close`, undefined, 'POST'),
    );
    try {
      return deviceActionResultSchema.parse(await connection.request(request));
    } catch {
      return { status: 'unknown' as const };
    } finally {
      await connection.close();
    }
  }
  async openConnection(device: RemoteDevice, tool: ConnectionTool): Promise<void> {
    if (!device[tool]?.available) throw new Error('This device connection is unavailable.');
    const url = new URL(
      tool === 'terminal' ? '/terminal' : tool === 'nyx' ? '/nyx' : '/desktop',
      this.apiBaseUrl,
    );
    if (tool === 'desktop' && !device.desktop.controlAvailable)
      url.searchParams.set('viewOnly', '1');
    url.searchParams.set('device', device.id);
    url.searchParams.set('name', device.name);
    navigateBrowserTab(url.href);
  }
  async openNyx(device: RemoteDevice) {
    if (!device.nyx?.available)
      throw new Error('Nyx access has not been granted for this online device.');
    const lease = await this.#get('/browser/sessions', undefined, 'POST', 'throw', {
      deviceId: device.id,
      capabilities: device.nyx.controlAvailable ? ['nyx.read', 'nyx.control'] : ['nyx.read'],
    });
    return browserNyxConnection(this.apiBaseUrl, lease, (id) =>
      this.#get(`/browser/sessions/${id}/close`, undefined, 'POST'),
    );
  }
}
